import createStoreFromReducer from 'js/lib/create-store-from-reducer';

import type { SubmissionAppState } from 'bundles/assess-common/types/SubmissionAppState';
import { ActionNames } from 'bundles/peer/actions/constants';

import _t from 'i18n!nls/peer';

function getErrorMessage(code?: number, responseJson?: { message?: string | null }): string {
  // The backend rejects draft saves once the learner has already submitted,
  // returning a 400 with a message like "Users may not save drafts when they
  // have submitted submissions." (errorCode is null, so we key off the message).
  // Surface a clear explanation instead of the generic "Save error." (ING-7135).
  if (responseJson?.message?.includes('may not save drafts')) {
    return _t(
      'You have already submitted this assignment, so it can no longer be edited. Contact your support team if you need to resubmit.'
    );
  }

  const CODE_TO_ERROR_MESSAGE = {
    '413': _t('Your submission is too large to be saved. Remove some content and try again.'),
  };

  if (code) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return CODE_TO_ERROR_MESSAGE[code.toString()];
  } else {
    return '';
  }
}

type Action =
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_READY;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_SUCCESS;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVE_SUCCESS;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SAVING;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVING;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_ERROR;
      responseJson: any;
      statusCode?: number;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_SUCCESS;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SUBMITTING;
    }
  | {
      type: typeof ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_ERROR;
      responseJson: any;
      statusCode?: number;
    }
  | {
      type: typeof ActionNames.PEER_SUBMIT_LOCAL_UPDATE_DRAFT_SUBMISSION;
    };

const initialState: SubmissionAppState = {
  saveState: 'ready',
  submitState: 'ready',
  showValidation: false,
  errorMessage: undefined,
};

function peerSubmissionAppStateReducer(state: SubmissionAppState = initialState, action: Action): SubmissionAppState {
  switch (action.type) {
    case ActionNames.PEER_SUBMISSION_APP_STATE_READY:
      return {
        saveState: 'ready',
        submitState: 'ready',
        showValidation: false,
        errorMessage: undefined,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_SUCCESS:
      return {
        ...state,
        saveState: 'ready',
        showValidation: true,
        errorMessage: undefined,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVE_SUCCESS:
      return {
        ...state,
        saveState: 'ready',
        showValidation: false,
        errorMessage: undefined,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SAVING:
      return {
        ...state,
        saveState: 'saving',
        submitState: 'ready',
        showValidation: false,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVING:
      return {
        ...state,
        saveState: 'autoSaving',
        submitState: 'ready',
        showValidation: false,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_ERROR:
      return {
        ...state,
        saveState: 'saveError',
        showValidation: true,
        errorMessage: getErrorMessage(action.statusCode, action.responseJson),
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_SUCCESS:
      return {
        ...state,
        submitState: 'ready',
        showValidation: true,
        errorMessage: undefined,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SUBMITTING:
      return {
        ...state,
        submitState: 'submitting',
        showValidation: false,
      };

    case ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_ERROR:
      return {
        ...state,
        submitState: 'submitError',
        showValidation: true,
        // A submit first saves a draft; if that save already failed with a
        // specific message (e.g. the learner has already submitted), keep it
        // rather than clobbering it with an empty generic submit error (ING-7135).
        errorMessage: getErrorMessage(action.statusCode, action.responseJson) || state.errorMessage,
      };

    case ActionNames.PEER_SUBMIT_LOCAL_UPDATE_DRAFT_SUBMISSION:
      return state;

    default:
      return state;
  }
}

/**
 * -- Generated using Cursor --
 * Fluxible ReducerStore instance for managing the peer submission app state.
 *
 * This store tracks the state of saving and submitting peer assignment submissions, including
 * loading, error, and validation states. It is used to control UI feedback for save/submit actions
 * and to provide the current app state to connected components.
 *
 * State shape (SubmissionAppState):
 *   - saveState: 'ready' | 'saving' | 'autoSaving' | 'saveError'
 *   - submitState: 'ready' | 'submitting' | 'submitError'
 *   - showValidation: boolean
 *   - errorMessage?: string
 *
 * @returns {new (dispatcher: Dispatcher) => ReducerStore<SubmissionAppState>} Store interface with a `getState` method returning the current app state.
 *
 * @example
 *   import { store } from 'bundles/peer/reducers/PeerSubmissionAppStateReducer';
 *   const state = store.getState();
 *   // Use state.saveState, state.submitState, etc.
 */
export const store = createStoreFromReducer(
  peerSubmissionAppStateReducer,
  'PeerSubmissionAppStateStore',
  [
    ActionNames.PEER_SUBMISSION_APP_STATE_READY,

    ActionNames.PEER_SUBMISSION_APP_STATE_SAVING,
    ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVING,
    ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_ERROR,
    ActionNames.PEER_SUBMISSION_APP_STATE_SAVE_SUCCESS,
    ActionNames.PEER_SUBMISSION_APP_STATE_AUTO_SAVE_SUCCESS,

    ActionNames.PEER_SUBMISSION_APP_STATE_SUBMITTING,
    ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_SUCCESS,
    ActionNames.PEER_SUBMISSION_APP_STATE_SUBMIT_ERROR,

    ActionNames.PEER_SUBMIT_LOCAL_UPDATE_DRAFT_SUBMISSION,
  ],
  initialState
);

export const reducer = peerSubmissionAppStateReducer;
